import React from 'react';
import { trapFocus } from '@/utils/accessibility';

interface FocusManagerProps {
  children: React.ReactNode;
  enabled?: boolean;
  restoreFocus?: boolean;
  initialFocus?: string; // CSS selector
  className?: string;
}

export const FocusManager: React.FC<FocusManagerProps> = ({
  children,
  enabled = true,
  restoreFocus = true,
  initialFocus,
  className,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const previousActiveElement = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!enabled || !containerRef.current) return;

    if (restoreFocus) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }

    if (initialFocus) {
      const element = containerRef.current.querySelector(
        initialFocus
      ) as HTMLElement;
      if (element) {
        element.focus();
      }
    } else {
      const focusableElements = containerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as NodeListOf<HTMLElement>;

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    const cleanup = trapFocus(containerRef.current);

    return () => {
      cleanup();

      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [enabled, initialFocus, restoreFocus]);

  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
};

// Skip link component for keyboard navigation
interface SkipLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
}

export const SkipLink: React.FC<SkipLinkProps> = ({
  href,
  children,
  className,
}) => {
  return (
    <a
      href={href}
      className={`
        sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 
        bg-primary-600 text-white px-4 py-2 rounded-md z-50 
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${className || ''}
      `}
      onClick={(e) => {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          (target as HTMLElement).focus();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      }}
    >
      {children}
    </a>
  );
};

// Roving tabindex hook for managing focus in lists
export const useRovingTabIndex = (
  items: React.RefObject<HTMLElement>[],
  initialIndex = 0
) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          setCurrentIndex((prev) => (prev + 1) % items.length);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
          break;
        case 'Home':
          e.preventDefault();
          setCurrentIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setCurrentIndex(items.length - 1);
          break;
      }
    },
    [items.length]
  );

  React.useEffect(() => {
    const currentItem = items[currentIndex]?.current;
    if (currentItem) {
      currentItem.focus();
    }
  }, [currentIndex, items]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    currentIndex,
    setCurrentIndex,
    handleKeyDown,
  };
};
